/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { GreyText, LargeColumn, SmallColumn } from "~/components/Tables/styles";
import { dateFormat } from "~/constants";
import { NA_LABEL, updateReportColumnLabels } from "../const";
import { UpdateReportDetail } from "../types";
import { isNaLike } from "../utils";
import { UpdateReportStatusCell } from "./UpdateReportStatusCell";

const columnHelper = createColumnHelper<UpdateReportDetail>();

const renderTextValue = (value?: string | null) => {
  if (isNaLike(value)) {
    return <GreyText>{NA_LABEL}</GreyText>;
  }

  return value?.trim();
};

const renderTime = (value?: string | null) => {
  if (isNaLike(value)) {
    return <GreyText>{NA_LABEL}</GreyText>;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return <GreyText>{NA_LABEL}</GreyText>;
  }
  return parsed.format(dateFormat.dayMonthYearHourMinute);
};

const renderReleaseDate = (value?: string | null) => {
  if (isNaLike(value)) {
    return <GreyText>{NA_LABEL}</GreyText>;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return <GreyText>{NA_LABEL}</GreyText>;
  }
  return parsed.format(dateFormat.dayMonthYear);
};

const renderRebootAllowed = (value?: boolean | null) => {
  if (value === true) {
    return "Yes";
  }
  if (value === false) {
    return "No";
  }
  return <GreyText>{NA_LABEL}</GreyText>;
};

export const updatesTableColumns: ColumnDef<UpdateReportDetail>[] = [
  columnHelper.accessor("name", {
    header: updateReportColumnLabels.name,
    cell: (info) => info.getValue(),
    meta: LargeColumn,
  }),
  columnHelper.accessor((row) => row.time, {
    header: updateReportColumnLabels.time,
    cell: ({ getValue }) => renderTime(getValue()),
    meta: SmallColumn,
  }),
  columnHelper.accessor("patchPolicy", {
    header: updateReportColumnLabels.patchPolicy,
    cell: ({ getValue }) => renderTextValue(getValue()),
  }),
  columnHelper.accessor((row) => row.rebootAllowed, {
    id: "rebootAllowed",
    header: updateReportColumnLabels.rebootAllowed,
    cell: ({ getValue }) => renderRebootAllowed(getValue()),
  }),
  columnHelper.accessor("releaseDate", {
    header: updateReportColumnLabels.releaseDate,
    cell: ({ getValue }) => renderReleaseDate(getValue()),
    meta: SmallColumn,
  }),
  columnHelper.accessor("category", {
    header: updateReportColumnLabels.category,
    cell: ({ getValue }) => renderTextValue(getValue()),
    meta: SmallColumn,
  }),

  columnHelper.accessor("status", {
    header: updateReportColumnLabels.status,
    cell: ({ row }) => {
      return <UpdateReportStatusCell detail={row.original} />;
    },
    meta: SmallColumn,
  }),
] as never;
